/**
 * Chip-style tag input. Each entry is rendered as a removable chip;
 * the user types comma- or newline-separated values into the trailing
 * input. Pasting a multi-line list splits on `\n` automatically.
 */
import { useState, type KeyboardEvent } from 'react';

interface ChipInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  maxItems?: number;
}

export function ChipInput({ value, onChange, placeholder, maxItems }: ChipInputProps) {
  const [draft, setDraft] = useState('');

  function commit(raw: string) {
    const pieces = raw
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (pieces.length === 0) return;
    const merged = Array.from(new Set([...value, ...pieces]));
    onChange(maxItems ? merged.slice(0, maxItems) : merged);
    setDraft('');
  }

  function remove(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(draft);
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  function onPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text');
    if (text.includes('\n') || text.includes(',')) {
      e.preventDefault();
      commit(text);
    }
  }

  return (
    <div className="feluda-chip-input">
      {value.map((chip, idx) => (
        <span key={`${chip}-${idx}`} className="feluda-chip">
          {chip}
          <button
            type="button"
            aria-label={`Remove ${chip}`}
            onClick={() => remove(idx)}
            className="feluda-chip-remove"
          >
            ×
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        placeholder={value.length === 0 ? (placeholder ?? 'Add…') : ''}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onBlur={() => draft.trim() && commit(draft)}
        className="feluda-chip-input-field"
      />
    </div>
  );
}